import { config } from "../config";
import { ApiError } from "./errors";
import { callGateway, callGatewayBytes, type CallerIdentity } from "./gateway";

/**
 * Sign-in & security: the signed-in person's OWN authenticator app, from
 * radaccess /access/operator/{ns}/account/* (owner decision 2026-09-15:
 * Google Authenticator is optional, set up here, and asked after every
 * sign-in once enrolled).
 *
 * 🔑 NO USER ID TRAVELS. radaccess acts on the token's subject and nothing
 * else; the namespace in the path is only its containment. These calls must
 * never grow a userId parameter.
 */

export interface TotpRegistration {
  /** otpauth://totp/… -- what the QR code carries. */
  uri: string;
  /** The same key, for typing into the app by hand. */
  secret: string;
}

const accountPath = (namespace: string, leaf: string) => `/operator/${encodeURIComponent(namespace)}/account/${leaf}`;

export function isOtpauthUri(uri: unknown): uri is string {
  return typeof uri === "string" && uri.startsWith("otpauth://totp/");
}

/** "JBSW Y3DP EHPK 3PXP": easier to type and to check than one long run. */
export function groupKey(secret: string): string {
  return secret.replace(/\s+/g, "").replace(/(.{4})(?=.)/g, "$1 ");
}

export async function getAccountSecurity(identity: CallerIdentity, namespace: string, signal?: AbortSignal): Promise<{ totpEnrolled: boolean }> {
  const r = await callGateway<{ totpEnrolled?: boolean }>(identity, config().gatewayBase, accountPath(namespace, "security"), signal ? { signal } : {});
  return { totpEnrolled: r.data?.totpEnrolled === true };
}

export async function startTotpEnrolment(identity: CallerIdentity, namespace: string): Promise<TotpRegistration> {
  const r = await callGateway<Partial<TotpRegistration>>(identity, config().gatewayBase, accountPath(namespace, "totp"), { method: "POST" });
  const uri = r.data?.uri;
  const secret = r.data?.secret;
  // A registration without both halves cannot be scanned or typed: refuse it
  // rather than show a QR code that sets up nothing.
  if (!isOtpauthUri(uri) || typeof secret !== "string" || !secret) {
    throw new ApiError("unavailable", "The set-up answer from the server was incomplete.", 200);
  }
  return { uri, secret };
}

export async function confirmTotpEnrolment(identity: CallerIdentity, namespace: string, code: string): Promise<void> {
  await callGateway(identity, config().gatewayBase, accountPath(namespace, "totp/verify"), { method: "POST", body: { code: code.trim() } });
}

export async function removeTotp(identity: CallerIdentity, namespace: string, code: string): Promise<void> {
  await callGateway(identity, config().gatewayBase, accountPath(namespace, "totp/remove"), { method: "POST", body: { code: code.trim() } });
}

/** What the person is told when a call is refused. */
export function accountErrorText(err: unknown): string {
  if (err instanceof ApiError) {
    const code = (err.body as { error?: unknown } | null)?.error;
    if (err.status === 401 && code === "invalid_code") return "That code is not right. Enter the code the app shows now.";
    if (err.status === 400 && code === "invalid_code_format") return "Enter the 6-digit code from the app.";
    if (err.status === 429) return "Too many attempts. Wait a few minutes and try again.";
    if (err.status === 423) return "This account is locked after too many attempts. Ask your administrator to unlock it.";
    if (err.status === 409 && code === "totp_already_enrolled") return "An authenticator app is already set up for this account.";
    if (err.status === 409 && code === "totp_not_enrolled") return "No authenticator app is set up for this account.";
    if (err.status === 403) return "This account cannot manage an authenticator app here.";
  }
  return "That could not be completed. Try again in a moment.";
}

/**
 * Settings › My contact details (owner 2026-10-25: "I don't know even mobile
 * number and don't know where it is saved"): the caller's OWN mobile and
 * email as ZITADEL holds them. Same rule as above -- no user id travels.
 */
export interface ContactDetails {
  /** +91XXXXXXXXXX, or "" when none is stored. */
  mobile: string;
  mobileVerified: boolean;
  /** "" when none (a sign-up placeholder reads as none). */
  email: string;
  emailVerified: boolean;
  /** Owner 2026-10-25: this login must use a second factor (radaccess mfa.go). */
  twoFactorRequired: boolean;
  /** ...and has no second way yet: the console shows only the set-up screen. */
  twoFactorSetupNeeded: boolean;
  /** External accounts (Google) linked to this login. */
  links: LinkedAccount[];
  /** Providers that can be linked from Settings. */
  providers: { id: string; name: string }[];
  /** Photo (owner 2026-10-26): the picture's hash, "" for none. */
  avatar: string;
}

export interface LinkedAccount {
  idpId: string;
  provider: string;
  externalUserId: string;
  name: string;
}

export async function getContactDetails(identity: CallerIdentity, namespace: string, signal?: AbortSignal): Promise<ContactDetails> {
  const r = await callGateway<Partial<ContactDetails>>(identity, config().gatewayBase, accountPath(namespace, "contact"), signal ? { signal } : {});
  const d = r.data ?? {};
  const str = (v: unknown) => (typeof v === "string" ? v : "");
  return {
    mobile: str(d.mobile),
    mobileVerified: d.mobileVerified === true,
    email: str(d.email),
    emailVerified: d.emailVerified === true,
    twoFactorRequired: d.twoFactorRequired === true,
    twoFactorSetupNeeded: d.twoFactorSetupNeeded === true,
    links: (Array.isArray(d.links) ? d.links : [])
      .map((l) => ({ idpId: str(l?.idpId), provider: str(l?.provider), externalUserId: str(l?.externalUserId), name: str(l?.name) }))
      .filter((l) => l.idpId && l.externalUserId),
    providers: (Array.isArray(d.providers) ? d.providers : []).map((p) => ({ id: str(p?.id), name: str(p?.name) })).filter((p) => p.id),
    avatar: /^[0-9a-f]{64}$/.test(str(d.avatar)) ? str(d.avatar) : "",
  };
}

/** Photo: the caller's own picture, or null when they have none. */
export async function getOwnAvatar(identity: CallerIdentity, namespace: string, signal?: AbortSignal): Promise<Blob | null> {
  try {
    return await callGatewayBytes(identity, config().gatewayBase, accountPath(namespace, "avatar"), signal ? { signal } : {});
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/** Stores a JPEG/PNG data URL (already shrunk) as the caller's picture; answers its hash. */
export async function setOwnAvatar(identity: CallerIdentity, namespace: string, dataUrl: string): Promise<string> {
  const r = await callGateway<{ avatar?: string }>(identity, config().gatewayBase, accountPath(namespace, "avatar"), { method: "POST", body: { image: dataUrl } });
  return typeof r.data?.avatar === "string" ? r.data.avatar : "";
}

export async function removeOwnAvatar(identity: CallerIdentity, namespace: string): Promise<void> {
  await callGateway(identity, config().gatewayBase, accountPath(namespace, "avatar/remove"), { method: "POST", body: {} });
}

export function avatarErrorText(err: unknown): string {
  if (err instanceof ApiError) {
    const code = (err.body as { error?: unknown } | null)?.error;
    if (code === "avatar_not_jpeg_or_png" || code === "avatar_unreadable") return "Choose a JPEG or PNG photo.";
    if (code === "avatar_too_large") return "That photo is too large. Choose a smaller one.";
    if (code === "avatar_bad_size") return "That picture is too small. Choose a photo at least 32 pixels wide.";
    if (code === "avatars_not_available_yet") return "Photos are not available yet. Try again later.";
  }
  if (err instanceof Error && err.message === "not-an-image") return "Choose a JPEG or PNG photo.";
  return "The photo could not be saved. Try again in a moment.";
}

/** Starts linking a provider: the browser goes to authUrl and comes back to /settings/security. */
export async function startProviderLink(identity: CallerIdentity, namespace: string, idpId: string): Promise<{ authUrl: string; ticket: string }> {
  const r = await callGateway<{ authUrl?: string; ticket?: string }>(identity, config().gatewayBase, accountPath(namespace, "idp/start"), { method: "POST", body: { idpId } });
  const authUrl = r.data?.authUrl ?? "";
  const ticket = r.data?.ticket ?? "";
  if (!/^https:\/\//.test(authUrl) || !ticket) throw new ApiError("unavailable", "The answer from the server was incomplete.", 200);
  return { authUrl, ticket };
}

/** Links the provider the browser came back from, with a fresh code to the caller's own mobile. */
export async function finishProviderLink(
  identity: CallerIdentity,
  namespace: string,
  input: { ticket: string; intentId: string; intentToken: string; mobile: string; accessToken: string },
): Promise<void> {
  await callGateway(identity, config().gatewayBase, accountPath(namespace, "idp/link"), { method: "POST", body: input });
}

export async function removeProviderLink(identity: CallerIdentity, namespace: string, idpId: string, externalUserId: string): Promise<void> {
  await callGateway(identity, config().gatewayBase, accountPath(namespace, "idp/remove"), { method: "POST", body: { idpId, externalUserId } });
}

/** Ends every trusted browser of the caller's login. */
export async function forgetTrustedBrowsers(identity: CallerIdentity, namespace: string): Promise<void> {
  await callGateway(identity, config().gatewayBase, accountPath(namespace, "browsers/forget"), { method: "POST" });
}

/** Marks the STORED mobile verified with a code sent by sendAccountCode(["sms"]). */
export async function verifyOwnMobile(identity: CallerIdentity, namespace: string, mobile: string, accessToken: string): Promise<void> {
  await callGateway(identity, config().gatewayBase, accountPath(namespace, "mobile/verify"), { method: "POST", body: { mobile, accessToken } });
}

/** A login with no mobile: a code by SMS to the number being added (owner 2026-10-25). */
export async function sendAddMobileCode(identity: CallerIdentity, namespace: string, mobile: string): Promise<{ requestId: string; resendIn: number; mobile: string }> {
  const r = await callGateway<{ requestId?: string; resendIn?: number; mobile?: string }>(
    identity, config().gatewayBase, accountPath(namespace, "mobile/code"), { method: "POST", body: { mobile } },
  );
  if (typeof r.data?.requestId !== "string" || !r.data.requestId) {
    throw new ApiError("unavailable", "The answer from the server was incomplete.", 200);
  }
  return { requestId: r.data.requestId, resendIn: r.data.resendIn ?? 30, mobile: r.data.mobile ?? "" };
}

/** Sets that number, verified, with requestId.code from the SMS. */
export async function addOwnMobile(identity: CallerIdentity, namespace: string, mobile: string, requestId: string, code: string): Promise<void> {
  await callGateway(identity, config().gatewayBase, accountPath(namespace, "mobile"), {
    method: "POST", body: { mobile, accessToken: `${requestId}.${code.trim()}` },
  });
}

/** Emails a code to the address being added; the code never comes back here. */
export async function sendEmailCheckCode(identity: CallerIdentity, namespace: string, email: string): Promise<{ requestId: string; resendIn: number; email: string }> {
  const r = await callGateway<{ requestId?: string; resendIn?: number; email?: string; status?: string }>(
    identity, config().gatewayBase, accountPath(namespace, "email/code"), { method: "POST", body: { email: email.trim() } },
  );
  if (r.data?.status === "already_verified") throw new ApiError("bad-request", "already_verified", 409, null, { error: "already_verified" });
  if (typeof r.data?.requestId !== "string" || !r.data.requestId) {
    throw new ApiError("unavailable", "The answer from the server was incomplete.", 200);
  }
  return { requestId: r.data.requestId, resendIn: r.data.resendIn ?? 30, email: r.data.email ?? "" };
}

/** Sets the email, verified, with requestId.code from the emailed code. */
export async function setOwnEmail(identity: CallerIdentity, namespace: string, email: string, requestId: string, code: string): Promise<void> {
  await callGateway(identity, config().gatewayBase, accountPath(namespace, "email"), {
    method: "POST", body: { email: email.trim(), accessToken: `${requestId}.${code.trim()}` },
  });
}

/** A plain address with a dot in the domain; the server checks again. */
export function looksLikeEmail(v: string): boolean {
  return /^[^\s@<>()",;]+@[^\s@<>()",;]+\.[^\s@<>()",;]+$/.test(v.trim());
}

/** What the person is told when a contact change is refused. */
export function contactErrorText(err: unknown): string {
  if (err instanceof ApiError) {
    const code = (err.body as { error?: unknown } | null)?.error;
    if (err.status === 401) return "That code is not right or has expired. Send a new code.";
    if (code === "email_in_use") return "Another login already uses this email address. Use a different one.";
    if (code === "already_verified") return "This email address is already verified on your login.";
    if (code === "invalid_email") return "Enter one email address, like name@example.com.";
    if (code === "mobile_not_yours") return "That is not the mobile number on your login.";
    if (code === "no_mobile") return "Your login has no mobile number yet. Add one above.";
    if (code === "mobile_in_use") return "Another login already uses this mobile number. Use a different one.";
    if (code === "mobile_exists") return "Your login already has a mobile number. Ask your administrator to change it.";
    if (code === "invalid_mobile") return "Enter a 10-digit Indian mobile number.";
    if (code === "link_refused") return "That Google account is already linked to another login.";
    if (code === "account_email_unverified") return "Verify your email address first: the link notice is sent there.";
    if (code === "idp_failed") return "The Google sign-in did not finish. Try Link Google again.";
    if (code === "ticket_expired") return "That took too long. Try Link Google again.";
    if (err.status === 429) return "Too many codes. Wait a little and try again.";
    if (err.status === 503) return "Codes are not available right now. Try again later.";
  }
  return "That could not be completed. Try again in a moment.";
}
